import { useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import CloseIcon from '@mui/icons-material/Close';
import { updateCatalogContact } from './catalogInterestApi';
import { useAuthStore } from '../../store/authStore';
import PhoneWithCountryField from '../shared/PhoneWithCountryField';
import { isValidFullPhone, normPhoneDigits } from '../../utils/phoneCountryCodes';

export default function CatalogWhatsAppModal({
  open,
  onClose,
  onSaved,
  productPreview,
}) {
  const [whatsapp, setWhatsapp] = useState('');
  const [phoneError, setPhoneError] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (open) {
      setWhatsapp(normPhoneDigits(user?.catalogWhatsapp) || '');
      setPhoneError(false);
      setError('');
    }
  }, [open, user?.catalogWhatsapp]);

  const handleClose = () => {
    setError('');
    setPhoneError(false);
    onClose?.();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValidFullPhone(whatsapp)) {
      setPhoneError(true);
      setError('Ingresa tu WhatsApp con código de país (ej. +591 y tu número)');
      return;
    }
    setPhoneError(false);

    setLoading(true);
    try {
      const data = await updateCatalogContact(whatsapp);
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      const merged = {
        ...stored,
        catalogWhatsapp: data?.catalogWhatsapp || whatsapp,
      };
      localStorage.setItem('user', JSON.stringify(merged));
      useAuthStore.setState({ user: merged });
      onSaved?.({ skipped: false, whatsapp });
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo guardar el contacto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open}>
        <Box
          component="form"
          onSubmit={handleSave}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '92%', sm: 440 },
            bgcolor: '#141820',
            color: '#f3f4f6',
            borderRadius: 3,
            boxShadow: '0 24px 64px rgba(0,0,0,.45)',
            border: '1px solid rgba(255,255,255,.08)',
            p: 3,
            outline: 'none',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
            <Box pr={2}>
              <Typography variant="h6" fontWeight={700}>
                Tu WhatsApp
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, color: 'rgba(255,255,255,.65)' }}>
                Necesario para guardar el interés. La tienda podrá escribirte con el enlace directo
                a WhatsApp.
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} sx={{ color: 'rgba(255,255,255,.6)' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          {productPreview?.nombre && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.5)', display: 'block', mb: 1.5 }}>
              Guardando interés en: {productPreview.nombre}
            </Typography>
          )}

          {user?.username && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.45)', display: 'block', mb: 1 }}>
              Cuenta: {user.username}
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <PhoneWithCountryField
            label="WhatsApp"
            required
            theme="dark"
            value={whatsapp}
            onChange={(v) => {
              setWhatsapp(v);
              setPhoneError(false);
            }}
            error={phoneError}
            errorText="Selecciona país y escribe tu número (mín. 7 dígitos locales)"
            helperText="Por defecto Bolivia (+591). Puedes buscar otro país en la lista."
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              mt: 2,
              py: 1.1,
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: '#f59e0b',
              color: '#111',
              '&:hover': { bgcolor: '#d97706' },
            }}
          >
            {loading ? 'Guardando…' : 'Guardar y continuar'}
          </Button>
        </Box>
      </Fade>
    </Modal>
  );
}
